import * as yfiles from '@ardoq/yfiles';
import {
  GenericLayoutData,
  INode,
  Insets,
  LayoutKeys,
  Size,
} from '@ardoq/yfiles';
import {
  Arrow,
  BackgroundVisualGroup,
  LayoutBounds,
  DashedStroke,
  DashStyle,
  EdgeSegmentLabelLayout,
  EdgeStyle,
  ExteriorLabelLayout,
  CanvasNodeStyle,
  CanvasGroupNodeStyle,
  CircularLayoutAlgorithmOptions,
  Font,
  GraphItemType,
  HierarchicalLayoutAlgorithmOptions,
  HierarchicalLayoutData,
  IncrementalHierarchicalLayoutData,
  OrthogonalLayoutAlgorithmOptions,
  RadialLayoutAlgorithmOptions,
  TreeLayoutAlgorithmOptions,
  IndicatorEdgeStyle,
  IndicatorNodeStyle,
  InputMode,
  InteriorLabelLayout,
  LabelLayout,
  LabelStyle,
  LayoutAlgorithm,
  DotsNodeStyle,
  NodeDot,
  MetaballBackgroundGroup,
  NodeStyle,
  OrganicLayoutAlgorithmOptions,
  OrganicLayoutData,
  PartialLayoutAlgorithmData,
  PartialLayoutAlgorithmOptions,
  PolylineEdgeStyle,
  ShapeNodeStyle,
  SolidStroke,
  Stroke,
  SVGNodeStyle,
  LabelIcon,
} from './props';
import {
  createLabelIconSVG,
  createLabelTextSVG,
  createSVGElement,
  LABEL_ICON_SIZE,
  translateNodeSVG,
} from './svg';
import { labelFootprint } from './labelMeasurement';
import { AccessibleNavigationInputMode } from './AccessibleNavigationInputMode';

const constructFont = (font: Font) =>
  new yfiles.Font({
    fontFamily: font.family,
    fontWeight: font.weight,
    fontSize: font.size,
  });

const constructExteriorLabelLayoutParameter = (layout: ExteriorLabelLayout) => {
  const model = new yfiles.ExteriorNodeLabelModel({ margins: layout.insets });
  return model.createParameter(layout.position);
};

const constructInteriorLabelLayoutParameter = (
  layout: InteriorLabelLayout
): yfiles.ILabelModelParameter => {
  const model = new yfiles.InteriorNodeLabelModel();
  return model.createParameter(layout.position);
};

const constructEdgeSegmentLabelLayoutParameter = (
  layout: EdgeSegmentLabelLayout
): yfiles.ILabelModelParameter => {
  const model = new yfiles.EdgeSegmentLabelModel();
  switch (layout.position) {
    case 'center':
      return model.createParameterFromCenter();
  }
};

export const constructLabelLayoutParameter = (
  layout: LabelLayout
): yfiles.ILabelModelParameter => {
  switch (layout.type) {
    case 'exterior':
      return constructExteriorLabelLayoutParameter(layout);
    case 'interior':
      return constructInteriorLabelLayoutParameter(layout);
    case 'edge-segment':
      return constructEdgeSegmentLabelLayoutParameter(layout);
  }
};

class IconTextLabelStyle extends yfiles.LabelStyleBase {
  constructor(
    private readonly wrappedStyle: yfiles.LabelStyle,
    private readonly icon: LabelIcon,
    private readonly textFill: string,
    private readonly font: Font
  ) {
    super();
  }

  protected createVisual(
    context: yfiles.IRenderContext,
    label: yfiles.ILabel
  ): yfiles.SvgVisual {
    const layout = label.layout;
    const g = createSVGElement('g');

    const transform = yfiles.LabelStyleBase.createLayoutTransform(
      context,
      layout,
      true
    );
    g.setAttribute('transform', transform.toSvgTransform());

    const iconWidth = this.icon.size?.width ?? LABEL_ICON_SIZE;
    const iconHeight = this.icon.size?.height ?? LABEL_ICON_SIZE;

    const iconSvg = createLabelIconSVG({
      icon: this.icon,
      width: iconWidth,
      height: iconHeight,
    });

    // Vertically center the icon within the label
    const iconOffsetY = (layout.height - iconHeight) / 2;
    if (iconOffsetY !== 0) {
      iconSvg.setAttribute('transform', `translate(0, ${iconOffsetY})`);
    }

    g.appendChild(iconSvg);

    if (label.text) {
      g.appendChild(
        createLabelTextSVG({
          text: label.text,
          fontFamily: this.wrappedStyle.font.fontFamily,
          fontSize: this.wrappedStyle.font.fontSize,
          fontWeight: `${this.wrappedStyle.font.fontWeight}`,
          fill: this.textFill,
          height: layout.height,
        })
      );
    }

    return new yfiles.SvgVisual(g);
  }

  protected getPreferredSize(label: yfiles.ILabel): yfiles.Size {
    const { padding } = this.wrappedStyle;
    const isIconOnly = !label.text;
    const iconSize = this.icon.size;

    if (isIconOnly && iconSize) {
      // Icon-only: label size = icon size + padding
      return new yfiles.Size(
        iconSize.width + padding.horizontalInsets,
        iconSize.height + padding.verticalInsets
      );
    }

    // Icon+text or icon-only without explicit size: calculate from content
    const { width, height } = labelFootprint({
      font: this.font,
      text: label.text,
      hasIcon: true,
      iconSize,
    });
    return new yfiles.Size(
      width + padding.horizontalInsets,
      height + padding.verticalInsets
    );
  }
}

export const constructLabelStyle = (style: LabelStyle): yfiles.ILabelStyle => {
  const baseStyle = new yfiles.LabelStyle({
    font: constructFont(style.font),
    textFill: style.textFill,
    padding: style.insets,
  });

  if (style.icon) {
    return new IconTextLabelStyle(
      baseStyle,
      style.icon,
      style.textFill,
      style.font
    );
  }

  return baseStyle;
};

class SVGStyle extends yfiles.NodeStyleBase {
  constructor(private svg: SVGElement) {
    super();
  }

  protected createVisual(
    _context: yfiles.IRenderContext,
    node: yfiles.INode
  ): yfiles.SvgVisual | null {
    const layout = node.layout;
    return new yfiles.SvgVisual(translateNodeSVG(this.svg, layout.x, layout.y));
  }
}

const constructVoidNodeStyle = () => yfiles.INodeStyle.VOID_NODE_STYLE;

const constructSVGNodeStyle = (style: SVGNodeStyle) => new SVGStyle(style.svg);

const constructShapeNodeStyle = (style: ShapeNodeStyle) => {
  if (style.shape === 'round-rectangle' && style.cornerRadius !== undefined) {
    return new yfiles.RectangleNodeStyle({
      cornerStyle: 'round',
      cornerSize: style.cornerRadius,
      scaleCornerSize: false,
      fill: style.fill,
      stroke: constructStroke(style.stroke),
    });
  }
  return new yfiles.ShapeNodeStyle({
    shape: style.shape,
    fill: style.fill,
    stroke: constructStroke(style.stroke),
  });
};

const DOT_RADIUS = 5;
const DOT_SPACING = 14;
const DOT_OFFSET_Y = 12;

const positionDot = (
  dot: SVGCircleElement,
  layout: LayoutBounds,
  index: number,
  total: number
): void => {
  const centerX = layout.x + layout.width / 2;
  const bottomY = layout.y + layout.height;
  dot.setAttribute(
    'cx',
    String(centerX + (index - (total - 1) / 2) * DOT_SPACING)
  );
  dot.setAttribute('cy', String(bottomY + DOT_OFFSET_Y));
  dot.setAttribute('r', String(DOT_RADIUS));
};

type DotsCache = {
  innerVisual: yfiles.Visual | null;
  dotElements: SVGCircleElement[];
  /**
   * The dots of the style that rendered last. The hover listeners are attached
   * once and read the callback from here, so they never call a stale closure
   * from an earlier render.
   */
  dots: NodeDot[];
};

const DOTS_CACHE_KEY = '__dotsCache';

type CachedDotsVisual = yfiles.SvgVisual & { [DOTS_CACHE_KEY]?: DotsCache };

/**
 * Wraps another node style and renders a row of dots below the node.
 *
 * `updateVisual` patches the existing elements rather than rebuilding them.
 * That matters for {@link NodeDot.onHover}: a hover handler that recolors the
 * graph triggers a repaint, and rebuilding would destroy the very circle the
 * pointer is over. The browser then fires `mouseleave` for the removed element
 * and — with the pointer still, since nothing moved — no `mouseenter` for its
 * replacement, so the hover would drop out on the frame it took effect.
 */
class DotsStyle extends yfiles.NodeStyleBase {
  constructor(
    private readonly inner: yfiles.INodeStyle,
    private readonly dots: NodeDot[]
  ) {
    super();
  }

  protected createVisual(
    context: yfiles.IRenderContext,
    node: yfiles.INode
  ): yfiles.SvgVisual | null {
    const group = createSVGElement('g');
    const innerVisual = this.inner.renderer
      .getVisualCreator(node, this.inner)
      .createVisual(context);
    if (innerVisual instanceof yfiles.SvgVisual && innerVisual.svgElement) {
      group.appendChild(innerVisual.svgElement);
    }

    const cache: DotsCache = {
      innerVisual,
      dotElements: [],
      dots: this.dots,
    };

    const dotsGroup = createSVGElement('g');
    this.dots.forEach((_, index) => {
      const dot = createSVGElement('circle') as SVGCircleElement;
      positionDot(dot, node.layout, index, this.dots.length);
      dot.setAttribute('stroke', 'white');
      dot.setAttribute('stroke-width', '1.5');
      dot.addEventListener('mouseenter', () =>
        cache.dots[index]?.onHover?.(true)
      );
      dot.addEventListener('mouseleave', () =>
        cache.dots[index]?.onHover?.(false)
      );
      const swallowIfClickable = (event: Event) => {
        if (cache.dots[index]?.onClick) event.stopPropagation();
      };
      dot.addEventListener('pointerdown', swallowIfClickable);
      dot.addEventListener('mousedown', swallowIfClickable);
      dot.addEventListener('click', event => {
        const onClick = cache.dots[index]?.onClick;
        if (!onClick) return;
        event.stopPropagation();
        onClick();
      });
      dotsGroup.appendChild(dot);
      cache.dotElements.push(dot);
    });
    group.appendChild(dotsGroup);

    this.paintDots(cache);

    const visual = new yfiles.SvgVisual(group) as CachedDotsVisual;
    visual[DOTS_CACHE_KEY] = cache;
    return visual;
  }

  protected updateVisual(
    context: yfiles.IRenderContext,
    oldVisual: yfiles.Visual,
    node: yfiles.INode
  ): yfiles.Visual | null {
    const cache = (oldVisual as CachedDotsVisual | null)?.[DOTS_CACHE_KEY];
    if (
      !(oldVisual instanceof yfiles.SvgVisual) ||
      !cache ||
      cache.dotElements.length !== this.dots.length
    ) {
      return this.createVisual(context, node);
    }

    const updatedInner = this.inner.renderer
      .getVisualCreator(node, this.inner)
      .updateVisual(context, cache.innerVisual);
    if (updatedInner !== cache.innerVisual) {
      if (
        !(updatedInner instanceof yfiles.SvgVisual) ||
        !updatedInner.svgElement
      ) {
        return this.createVisual(context, node);
      }
      if (
        cache.innerVisual instanceof yfiles.SvgVisual &&
        cache.innerVisual.svgElement
      ) {
        oldVisual.svgElement.replaceChild(
          updatedInner.svgElement,
          cache.innerVisual.svgElement
        );
      } else {
        oldVisual.svgElement.prepend(updatedInner.svgElement);
      }
      cache.innerVisual = updatedInner;
    }

    cache.dots = this.dots;
    cache.dotElements.forEach((dot, index) =>
      positionDot(dot, node.layout, index, this.dots.length)
    );
    this.paintDots(cache);

    return oldVisual;
  }

  private paintDots(cache: DotsCache): void {
    cache.dotElements.forEach((dot, index) => {
      const current = cache.dots[index];
      if (!current) return;
      dot.setAttribute('fill', current.color);
      const isInteractive = Boolean(current.onHover ?? current.onClick);
      dot.style.pointerEvents = isInteractive ? 'all' : 'none';
      dot.style.cursor = current.onClick ? 'pointer' : '';
    });
  }
}

const constructDotsNodeStyle = (style: DotsNodeStyle) =>
  new DotsStyle(constructNodeStyle(style.wrapped), style.dots);

/**
 * Renders a {@link CanvasNodeStyle} via a yFiles `HtmlCanvasVisual`. The 2D
 * context is already in world space, so the consumer's `render` draws using the
 * node's live layout bounds. `render` is invoked on every canvas repaint, so it
 * always reflects the node's current position.
 */
class CanvasNodeVisual extends yfiles.HtmlCanvasVisual {
  constructor(
    private readonly style: CanvasNodeStyle | CanvasGroupNodeStyle,
    private readonly node: yfiles.INode
  ) {
    super();
  }

  render(context: yfiles.IRenderContext, ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.node.layout;
    const bounds = { x, y, width, height };
    // Only a `canvas-group` can fold: a folded group becomes a non-group "folder" node,
    // drawn by its own `renderCollapsed` path. Leaf `canvas` nodes skip the folding
    // lookup entirely (never collapsed).
    const collapsed =
      this.style.type === 'canvas-group' &&
      (context.canvasComponent instanceof yfiles.GraphComponent
        ? context.canvasComponent.graph.foldingView
        : null
      )?.isExpanded(this.node) === false;
    if (
      collapsed &&
      this.style.type === 'canvas-group' &&
      this.style.renderCollapsed
    ) {
      this.style.renderCollapsed(ctx, bounds);
      return;
    }
    this.style.render(ctx, bounds);
  }
}

class CanvasNodeStyleImpl extends yfiles.NodeStyleBase {
  constructor(private readonly style: CanvasNodeStyle | CanvasGroupNodeStyle) {
    super();
  }

  protected createVisual(_context: yfiles.IRenderContext, node: yfiles.INode) {
    return new CanvasNodeVisual(this.style, node);
  }
}

const constructCanvasNodeStyle = (
  style: CanvasNodeStyle | CanvasGroupNodeStyle
) => new CanvasNodeStyleImpl(style);

export const constructNodeStyle = (style: NodeStyle): yfiles.INodeStyle => {
  switch (style.type) {
    case 'void':
      return constructVoidNodeStyle();
    case 'svg':
      return constructSVGNodeStyle(style);
    case 'shape':
      return constructShapeNodeStyle(style);
    case 'dots':
      return constructDotsNodeStyle(style);
    case 'canvas':
    case 'canvas-group':
      return constructCanvasNodeStyle(style);
  }
};

/**
 * Creates a yFiles folding view (a `FoldingManager` over a fresh master graph). The
 * returned view's `.graph` is what the `GraphComponent` should display and what
 * graph-view edits — collapsed groups appear as folder nodes with their children
 * hidden. Used only when {@link GraphViewProps.collapsibleGroups} is enabled.
 */
export const createFoldingView = (): yfiles.IFoldingView =>
  new yfiles.FoldingManager(new yfiles.Graph()).createFoldingView();

const GROUP_EXPANDER_SIZE = 20;
const GROUP_EXPANDER_MARGIN = 6;

/**
 * World-space square for a collapsible group's expander chevron, anchored top-left
 * of the group header. The single source of truth shared by the consumer (which
 * paints the chevron here) and graph-view (which hit-tests clicks here to toggle
 * folding), so the affordance and its click target never drift apart.
 */
export const getGroupExpanderRect = (layout: LayoutBounds): LayoutBounds => ({
  x: layout.x + GROUP_EXPANDER_MARGIN,
  y: layout.y + GROUP_EXPANDER_MARGIN,
  width: GROUP_EXPANDER_SIZE,
  height: GROUP_EXPANDER_SIZE,
});

const constructDashStyle = (dashStyle: DashStyle) =>
  new yfiles.DashStyle(dashStyle.pattern);

const constructSolidStroke = (stroke: SolidStroke) =>
  new yfiles.Stroke({
    fill: stroke.color,
    thickness: stroke.thickness,
  });

const constructDashedStroke = (stroke: DashedStroke) =>
  new yfiles.Stroke({
    fill: stroke.color,
    dashStyle: constructDashStyle(stroke.dashStyle),
    thickness: stroke.thickness,
  });

const constructStroke = (stroke: Stroke): yfiles.Stroke => {
  switch (stroke.line) {
    case 'solid':
      return constructSolidStroke(stroke);
    case 'dashed':
      return constructDashedStroke(stroke);
  }
};

const constructArrow = (arrow: Arrow) =>
  new yfiles.Arrow({
    type: arrow.type,
    cropLength: arrow.cropLength,
    fill: arrow.color,
    stroke: arrow.color,
  });

const constructPolylineEdgeStyle = (style: PolylineEdgeStyle) =>
  new yfiles.PolylineEdgeStyle({
    stroke: constructStroke(style.stroke),
    sourceArrow: constructArrow(style.sourceArrow),
    targetArrow: constructArrow(style.targetArrow),
    smoothingLength: style.smoothingLength,
  });

const constructVoidEdgeStyle = () => yfiles.IEdgeStyle.VOID_EDGE_STYLE;

export const constructEdgeStyle = (style: EdgeStyle): yfiles.IEdgeStyle => {
  switch (style.type) {
    case 'polyline':
      return constructPolylineEdgeStyle(style);
    case 'void':
      return constructVoidEdgeStyle();
  }
};

export const constructHierarchicLayout = (
  options: HierarchicalLayoutAlgorithmOptions
) =>
  new yfiles.HierarchicalLayout({
    layoutOrientation: options.layoutOrientation,
    defaultEdgeDescriptor: {
      routingStyleDescriptor: {
        defaultRoutingStyle: options.routingStyle,
      },
    },
    edgeLabelPlacement: options.integratedEdgeLabeling
      ? yfiles.EdgeLabelPlacement.INTEGRATED
      : yfiles.EdgeLabelPlacement.IGNORE,
    componentLayout: { enabled: options.componentLayoutEnabled },
    nodeLabelPlacement: options.considerNodeLabels
      ? yfiles.NodeLabelPlacement.CONSIDER
      : yfiles.NodeLabelPlacement.IGNORE,
  });

export const constructIncrementalHierarchicLayoutData = (
  data: IncrementalHierarchicalLayoutData
) => {
  const hierarchicalData = new yfiles.HierarchicalLayoutData();
  hierarchicalData.nodeMargins.constant = new yfiles.Insets(
    data.constant.nodeHalo
  );

  hierarchicalData.incrementalNodes = node =>
    !data.fixedNodes.includes(node?.tag?.node?.id);
  hierarchicalData.criticalEdgePriorities = edge =>
    data.fixedNodes.includes(edge.tag.edge.source) &&
    data.fixedNodes.includes(edge.tag.edge.target)
      ? 1
      : 0;
  const subgraphData = new yfiles.SubgraphLayoutStageData({
    subgraphNodes: node => data.fixedNodes.includes(node.tag.node.id),
    subgraphEdges: edge =>
      data.fixedNodes.includes(edge.tag.edge.source) &&
      data.fixedNodes.includes(edge.tag.edge.target),
  });
  const fixedNodes = new yfiles.LayoutAnchoringStageData({
    nodeAnchoringPolicies: node =>
      data.fixedNodes.includes(node.tag.node.id)
        ? yfiles.LayoutAnchoringPolicy.BOUNDS
        : yfiles.LayoutAnchoringPolicy.NONE,
  });

  const genericLayoutData = constructGenericLayoutData();

  return new yfiles.CompositeLayoutData(
    hierarchicalData,
    subgraphData,
    fixedNodes,
    genericLayoutData
  );
};

const widestNodeLabelOverhang = (graph: yfiles.IGraph): number =>
  graph.nodes.reduce(
    (widest, node) =>
      node.labels.reduce(
        (nodeWidest, label) =>
          Math.max(nodeWidest, (label.layout.width - node.layout.width) / 2),
        widest
      ),
    0
  );

// Node margins don't cover labels wider than their node, so the clearance for
// those comes from the minimum node distance.
const constructPartialLayout = (
  options: PartialLayoutAlgorithmOptions,
  graph: yfiles.IGraph
): yfiles.PartialLayout =>
  new yfiles.PartialLayout({
    coreLayout: constructHierarchicLayout(options.coreLayout),
    layoutOrientation: options.layoutOrientation,
    subgraphPlacement: options.subgraphPlacement,
    componentAssignmentStrategy: options.componentAssignmentStrategy,
    minimumNodeDistance:
      options.minimumNodeDistance + 2 * widestNodeLabelOverhang(graph),
    edgeRoutingStyle: options.edgeRoutingStyle,
    allowMovingFixedElements: options.allowMovingFixedElements,
  });

export const constructPartialLayoutData = (
  data: PartialLayoutAlgorithmData
) => {
  const partialNodeIds = new Set(data.partialNodes);
  const partialLayoutData = new yfiles.PartialLayoutData({
    scope: {
      nodes: (node: yfiles.INode) => partialNodeIds.has(node.tag?.node?.id),
    },
    nodeMargins: new yfiles.Insets(data.constant.nodeHalo),
  });

  const genericLayoutData = constructGenericLayoutData();

  return new yfiles.CompositeLayoutData(partialLayoutData, genericLayoutData);
};

const constructOrganicLayout = (
  options: OrganicLayoutAlgorithmOptions
): yfiles.OrganicLayout =>
  new yfiles.OrganicLayout({
    avoidNodeEdgeOverlap: options.avoidNodeEdgeOverlap,
    deterministic: options.deterministic,
    nodeLabelPlacement: options.considerNodeLabels
      ? yfiles.NodeLabelPlacement.CONSIDER
      : yfiles.NodeLabelPlacement.IGNORE,
  });

const constructOrganicLayoutData = (layoutData: OrganicLayoutData) => {
  const organicLayoutData = new yfiles.OrganicLayoutData();
  organicLayoutData.nodeMargins.constant = new yfiles.Insets(
    layoutData.constant.nodeHalo
  );

  const genericLayoutData = constructGenericLayoutData();

  return new yfiles.CompositeLayoutData(organicLayoutData, genericLayoutData);
};

const constructCircularLayout = (
  options: CircularLayoutAlgorithmOptions
): yfiles.CircularLayout => {
  const circularLayout = new yfiles.CircularLayout();
  circularLayout.edgeBundling.defaultBundleDescriptor.bundled = Boolean(
    options.edgeBundling
  );
  circularLayout.edgeBundling.bundlingStrength = options.edgeBundling;
  return circularLayout;
};

const constructOrthogonalLayout = (
  options: OrthogonalLayoutAlgorithmOptions
): yfiles.OrthogonalLayout =>
  new yfiles.OrthogonalLayout({
    nodeLabelPlacement: options.considerNodeLabels
      ? yfiles.NodeLabelPlacement.CONSIDER
      : yfiles.NodeLabelPlacement.IGNORE,
  });

const constructTreeLayout = (
  options: TreeLayoutAlgorithmOptions
): yfiles.ILayoutAlgorithm => {
  // TreeReductionStage first reduces an arbitrary graph to a spanning tree;
  // without it yFiles' TreeLayout only accepts strict trees (and times out on
  // anything else).
  const treeReduction = new yfiles.TreeReductionStage(
    new yfiles.TreeLayout({
      nodeLabelPlacement: options.considerNodeLabels
        ? yfiles.NodeLabelPlacement.CONSIDER
        : yfiles.NodeLabelPlacement.IGNORE,
    })
  );
  // The non-tree edges (cross-references dropped to form the spanning tree) are left
  // unrouted by default — drawn as straight diagonals across the diagram. Route them
  // orthogonally so they read like every other edge.
  treeReduction.nonTreeEdgeRouter = new yfiles.EdgeRouter();
  return treeReduction;
};

const constructRadialLayout = (
  options: RadialLayoutAlgorithmOptions
): yfiles.RadialLayout => {
  const radialLayout = new yfiles.RadialLayout();
  radialLayout.edgeBundling.defaultBundleDescriptor.bundled = Boolean(
    options.edgeBundling
  );
  radialLayout.edgeBundling.bundlingStrength = options.edgeBundling;
  return radialLayout;
};

const constructCompactDiskLayout = (): yfiles.CompactDiskLayout =>
  new yfiles.CompactDiskLayout();

export const constructLayoutAlgorithm = (
  algorithm: LayoutAlgorithm,
  graph: yfiles.IGraph
): yfiles.ILayoutAlgorithm => {
  switch (algorithm.type) {
    case 'hierarchical':
      return constructHierarchicLayout(algorithm.options);
    case 'incremental-hierarchical':
      return constructHierarchicLayout(algorithm.options);
    case 'partial':
      return constructPartialLayout(algorithm.options, graph);
    case 'organic':
      return constructOrganicLayout(algorithm.options);
    case 'circular':
      return constructCircularLayout(algorithm.options);
    case 'orthogonal':
      return constructOrthogonalLayout(algorithm.options);
    case 'tree':
      return constructTreeLayout(algorithm.options);
    case 'radial':
      return constructRadialLayout(algorithm.options);
    case 'compact-disk':
      return constructCompactDiskLayout();
  }
};

/**
 * Group edges sharing a source (or target) node into a common bus, and weight
 * each edge's crossing cost by the size of its source + target groups so the
 * layout keeps the larger buses uncrossed (mirrors the Modernized Block Diagram).
 */
/** Edges with the same distinct reference key (color/line/arrows) bus together; others don't. */
const distinctReferenceKey = (edge: yfiles.IEdge): string => {
  const style = (edge.tag as { edge?: { style?: EdgeStyle } } | undefined)?.edge
    ?.style;
  if (!style || style.type !== 'polyline') return '';
  const { stroke, sourceArrow, targetArrow } = style;
  return `${stroke.line}|${stroke.color}|${sourceArrow.type}|${targetArrow.type}`;
};

const nodeGroupId = (node: yfiles.INode): string =>
  (node.tag as { node?: { id?: string } } | undefined)?.node?.id ?? '';

const countWithKey = (edges: Iterable<yfiles.IEdge>, key: string): number => {
  let count = 0;
  for (const edge of edges) {
    if (distinctReferenceKey(edge) === key) count++;
  }
  return count;
};

const applyEdgeGrouping = (
  data: yfiles.HierarchicalLayoutData,
  graph: yfiles.IGraph
): void => {
  // An edge joins a bus at exactly one end — whichever end has the larger group
  // of edges sharing its distinct reference key (color/line/arrows) at that
  // node, never both. Self-loops aren't grouped.
  const busSide = (edge: yfiles.IEdge): 'source' | 'target' | null => {
    if (edge.isSelfLoop) return null;
    const key = distinctReferenceKey(edge);
    const sourceSize = countWithKey(graph.outEdgesAt(edge.sourceNode), key);
    const targetSize = countWithKey(graph.inEdgesAt(edge.targetNode), key);
    return sourceSize >= targetSize ? 'source' : 'target';
  };

  data.sourceGroupIds = (edge: yfiles.IEdge) =>
    busSide(edge) === 'source'
      ? `from:${nodeGroupId(edge.sourceNode)}:${distinctReferenceKey(edge)}`
      : null;
  data.targetGroupIds = (edge: yfiles.IEdge) =>
    busSide(edge) === 'target'
      ? `to:${nodeGroupId(edge.targetNode)}:${distinctReferenceKey(edge)}`
      : null;
  data.edgeCrossingCosts = (edge: yfiles.IEdge) => {
    const side = busSide(edge);
    if (!side) return 1;
    const key = distinctReferenceKey(edge);
    const groupSize =
      side === 'source'
        ? countWithKey(graph.outEdgesAt(edge.sourceNode), key)
        : countWithKey(graph.inEdgesAt(edge.targetNode), key);
    return Math.max(groupSize, 1);
  };
};

export const constructHierarchicLayoutData = (
  layoutData: HierarchicalLayoutData,
  graph: yfiles.IGraph
) => {
  const hierarchicLayoutData = new yfiles.HierarchicalLayoutData();
  hierarchicLayoutData.nodeMargins.constant = new yfiles.Insets(
    layoutData.constant.nodeHalo
  );

  if (layoutData.groupEdges) {
    applyEdgeGrouping(hierarchicLayoutData, graph);
  }

  const genericLayoutData = constructGenericLayoutData();

  return new yfiles.CompositeLayoutData(
    hierarchicLayoutData,
    genericLayoutData
  );
};

export const constructLayoutData = (
  algorithm: LayoutAlgorithm,
  graph: yfiles.IGraph
): yfiles.LayoutData => {
  switch (algorithm.type) {
    case 'hierarchical':
      return constructHierarchicLayoutData(algorithm.data, graph);
    case 'incremental-hierarchical':
      return constructIncrementalHierarchicLayoutData(algorithm.data);
    case 'partial':
      return constructPartialLayoutData(algorithm.data);
    case 'organic':
      return constructOrganicLayoutData(algorithm.data);
    case 'circular':
    case 'orthogonal':
    case 'tree':
    case 'radial':
    case 'compact-disk':
      return constructGenericLayoutData();
  }
};

const toGraphItemType = (item: GraphItemType): yfiles.GraphItemTypes => {
  switch (item) {
    case 'node':
      return yfiles.GraphItemTypes.NODE;
    case 'edge':
      return yfiles.GraphItemTypes.EDGE;
  }
};

export const toGraphItemTypes = (
  items: GraphItemType[]
): yfiles.GraphItemTypes =>
  items
    .map(toGraphItemType)
    .reduce((acc, item) => acc | item, yfiles.GraphItemTypes.NONE);

type ConstructGraphInputModeArgs = {
  inputMode: InputMode;
  collapsibleGroups: boolean;
  navigableItems: yfiles.GraphItemTypes | null;
};

export const constructGraphInputMode = ({
  inputMode,
  collapsibleGroups,
  navigableItems,
}: ConstructGraphInputModeArgs): yfiles.GraphViewerInputMode => {
  const selectableItems = toGraphItemTypes(inputMode.selectableItems);
  // Lets yFiles treat folder nodes correctly (keyboard collapse/expand, hit
  // semantics). The primary toggle path is graph-view's own expander hit-test.
  const groupNavigation = collapsibleGroups
    ? {
        allowCollapseGroup: true,
        allowExpandGroup: true,
        fitContentAfterGroupActions: false,
      }
    : {};
  if (navigableItems === null) {
    return new yfiles.GraphViewerInputMode({
      selectableItems,
      navigationInputMode: groupNavigation,
    });
  }
  return new yfiles.GraphViewerInputMode({
    selectableItems,
    focusableItems: navigableItems,
    navigationInputMode: new AccessibleNavigationInputMode({
      ...groupNavigation,
      navigableItems,
      selectableItems: navigableItems & selectableItems,
    }),
  });
};

const DEFAULT_GROUP_CORNER_RADIUS = 10;

export const toGroupIndicatorStyle = (
  indicator: IndicatorNodeStyle
): IndicatorNodeStyle =>
  indicator.wrapped.type === 'shape'
    ? {
        ...indicator,
        wrapped: {
          type: 'shape',
          fill: indicator.wrapped.fill,
          stroke: indicator.wrapped.stroke,
          shape: 'round-rectangle',
          cornerRadius: DEFAULT_GROUP_CORNER_RADIUS + indicator.padding,
        },
      }
    : indicator;

export const constructNodeStyleIndicatorRenderer = (
  style: IndicatorNodeStyle
) => {
  return new yfiles.NodeStyleIndicatorRenderer({
    nodeStyle: constructNodeStyle(style.wrapped),
    zoomPolicy: style.zoomPolicy,
    margins: style.padding,
  });
};

export const constructEdgeStyleIndicatorRenderer = (
  style: IndicatorEdgeStyle
) => {
  return new yfiles.EdgeStyleIndicatorRenderer({
    edgeStyle: constructEdgeStyle(style.wrapped),
    zoomPolicy: style.zoomPolicy,
  });
};

const GROUP_PADDING = 5;

/**
 * A {@link CanvasGroupNodeStyle} container declares its own insets + minimum size
 * on its style (e.g. to reserve a header stripe drawn in the canvas). Returns the
 * style only for `canvas-group` nodes, so every other group keeps the label-based
 * behaviour below.
 */
const canvasGroupStyle = (node: INode): CanvasGroupNodeStyle | null => {
  const style = (node.tag as { node?: { style?: NodeStyle } } | undefined)?.node
    ?.style;
  return style?.type === 'canvas-group' ? style : null;
};

/** TODO: This only applies to metamodel styled graph. Configure group node padding and minimum size so the layout leaves room for labels. */
const constructGenericLayoutData = (): GenericLayoutData => {
  const data = new GenericLayoutData();
  data.addItemMapping(LayoutKeys.GROUP_NODE_PADDING_DATA_KEY, (node: INode) => {
    const canvasStyle = canvasGroupStyle(node);
    if (canvasStyle) {
      const { top, right, bottom, left } = canvasStyle.insets;
      return new Insets(top, right, bottom, left);
    }
    const labelHeight = Array.from(node.labels).reduce(
      (max, label) => Math.max(max, label.layout.height),
      0
    );
    return new Insets(
      GROUP_PADDING + labelHeight,
      GROUP_PADDING,
      GROUP_PADDING,
      GROUP_PADDING
    );
  });
  data.addItemMapping(
    LayoutKeys.MINIMUM_GROUP_NODE_SIZE_DATA_KEY,
    (node: INode) => {
      const canvasStyle = canvasGroupStyle(node);
      if (canvasStyle) {
        return new Size(canvasStyle.size.width, canvasStyle.size.height);
      }
      const labelWidth = Array.from(node.labels).reduce(
        (max, label) => Math.max(max, label.layout.width),
        0
      );
      return new Size(labelWidth + GROUP_PADDING * 2, 0);
    }
  );
  return data;
};

const DEFAULT_METABALL_OPACITY = 0.35;
const DEFAULT_METABALL_PADDING = 40;
const METABALL_BRIDGE_EXTRA = 40;
const METABALL_BLUR_STD_DEVIATION = 60;

const createMetaballFilter = (id: string): SVGFilterElement => {
  const filter = createSVGElement('filter');
  filter.setAttribute('id', id);
  filter.setAttribute('x', '-100%');
  filter.setAttribute('y', '-100%');
  filter.setAttribute('width', '300%');
  filter.setAttribute('height', '300%');

  const blur = createSVGElement('feGaussianBlur');
  blur.setAttribute('in', 'SourceGraphic');
  blur.setAttribute('stdDeviation', String(METABALL_BLUR_STD_DEVIATION));
  blur.setAttribute('result', 'blur');
  filter.appendChild(blur);

  const colorMatrix = createSVGElement('feColorMatrix');
  colorMatrix.setAttribute('in', 'blur');
  colorMatrix.setAttribute('type', 'matrix');
  colorMatrix.setAttribute(
    'values',
    '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 100 -50'
  );
  filter.appendChild(colorMatrix);

  return filter;
};

type MetaballCache = {
  circleElements: Map<string, SVGCircleElement>;
  lineElements: Map<string, SVGLineElement>;
};

const METABALL_CACHE_KEY = '__metaballCache';

type CachedVisual = yfiles.Visual & { [METABALL_CACHE_KEY]?: MetaballCache };

const nodeCenter = (layout: {
  x: number;
  y: number;
  width: number;
  height: number;
}) => ({
  cx: layout.x + layout.width / 2,
  cy: layout.y + layout.height / 2,
  r: Math.max(layout.width, layout.height) / 2,
});

class MetaballGroupStyle extends yfiles.BaseClass(yfiles.IVisualCreator) {
  constructor(
    private readonly group: MetaballBackgroundGroup,
    private readonly builder: yfiles.GraphBuilder,
    private readonly instanceId: string
  ) {
    super();
  }

  createVisual(_context: yfiles.IRenderContext): yfiles.Visual | null {
    const { group, builder, instanceId } = this;
    const padding = group.padding ?? DEFAULT_METABALL_PADDING;
    const opacity = group.opacity ?? DEFAULT_METABALL_OPACITY;
    const bridgeThickness = padding + METABALL_BRIDGE_EXTRA;
    const filterId = `metaball-${instanceId}-${group.id}`;

    const root = createSVGElement('g');

    const defs = createSVGElement('defs');
    defs.appendChild(createMetaballFilter(filterId));
    root.appendChild(defs);

    const circlesGroup = createSVGElement('g');
    circlesGroup.setAttribute('filter', `url(#${filterId})`);
    circlesGroup.setAttribute('opacity', String(opacity));

    const circleElements = new Map<string, SVGCircleElement>();
    for (const nodeId of group.nodeIds) {
      const node = builder.getNodeById<string>(nodeId);
      if (!node) continue;
      const { cx, cy, r } = nodeCenter(node.layout);
      const circle = createSVGElement('circle');
      circle.setAttribute('cx', String(cx));
      circle.setAttribute('cy', String(cy));
      circle.setAttribute('r', String(r + padding));
      circle.setAttribute('fill', group.color);
      circlesGroup.appendChild(circle);
      circleElements.set(nodeId, circle);
    }

    // Full mesh bridging: stroke between every pair of member nodes. The
    // blurred filter fuses the blobs into a single connected shape.
    const lineElements = new Map<string, SVGLineElement>();
    for (let i = 0; i < group.nodeIds.length; i++) {
      for (let j = i + 1; j < group.nodeIds.length; j++) {
        const sourceNodeId = group.nodeIds[i];
        const targetNodeId = group.nodeIds[j];
        const sourceNode = builder.getNodeById<string>(sourceNodeId);
        const targetNode = builder.getNodeById<string>(targetNodeId);
        if (!sourceNode || !targetNode) continue;
        const src = nodeCenter(sourceNode.layout);
        const tgt = nodeCenter(targetNode.layout);
        const line = createSVGElement('line');
        line.setAttribute('x1', String(src.cx));
        line.setAttribute('y1', String(src.cy));
        line.setAttribute('x2', String(tgt.cx));
        line.setAttribute('y2', String(tgt.cy));
        line.setAttribute('stroke', group.color);
        line.setAttribute('stroke-width', String(bridgeThickness));
        line.setAttribute('stroke-linecap', 'round');
        circlesGroup.appendChild(line);
        lineElements.set(`${sourceNodeId}::${targetNodeId}`, line);
      }
    }

    root.appendChild(circlesGroup);
    const visual = new yfiles.SvgVisual(root) as CachedVisual;
    visual[METABALL_CACHE_KEY] = { circleElements, lineElements };
    return visual;
  }

  updateVisual(
    context: yfiles.IRenderContext,
    oldVisual: yfiles.Visual | null
  ): yfiles.Visual | null {
    const cache = (oldVisual as CachedVisual | null)?.[METABALL_CACHE_KEY];
    if (!oldVisual || !cache) return this.createVisual(context);

    const { group, builder } = this;
    const padding = group.padding ?? DEFAULT_METABALL_PADDING;

    for (const [nodeId, circle] of cache.circleElements) {
      const node = builder.getNodeById<string>(nodeId);
      if (!node) continue;
      const { cx, cy, r } = nodeCenter(node.layout);
      circle.setAttribute('cx', String(cx));
      circle.setAttribute('cy', String(cy));
      circle.setAttribute('r', String(r + padding));
    }

    for (const [key, line] of cache.lineElements) {
      const [sourceNodeId, targetNodeId] = key.split('::');
      const sourceNode = builder.getNodeById<string>(sourceNodeId);
      const targetNode = builder.getNodeById<string>(targetNodeId);
      if (!sourceNode || !targetNode) continue;
      const src = nodeCenter(sourceNode.layout);
      const tgt = nodeCenter(targetNode.layout);
      line.setAttribute('x1', String(src.cx));
      line.setAttribute('y1', String(src.cy));
      line.setAttribute('x2', String(tgt.cx));
      line.setAttribute('y2', String(tgt.cy));
    }

    return oldVisual;
  }
}

export const constructBackgroundVisual = (
  group: BackgroundVisualGroup,
  builder: yfiles.GraphBuilder,
  instanceId: string
): yfiles.IVisualCreator => {
  switch (group.type) {
    case 'metaball':
      return new MetaballGroupStyle(group, builder, instanceId);
  }
};
